import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { msalService } from './services/msalService';
import { oidcService } from './services/oidcService';
import { OIDC_ENABLED } from './config/oidcConfig';
import { AuthProvider, useAuth } from './context/AuthContext';
import { UnifiedSignalRProvider } from './contexts/UnifiedSignalRContext';
import { UserFavoritesProvider } from './contexts/UserFavoritesContext';
import { ChatProvider } from './contexts/ChatContext';
import { authLogger } from './utils/logger';
import { healthService } from './services/healthService';
// import { SignalRMonitoringPanel } from './components/debug/SignalRMonitoringPanel';
import Layout from './Layout';
import AdminLayout from './AdminLayout';
import Dashboard from './pages/Dashboard';
import ChatPage from './pages/ChatPage';
import UserProfile from './pages/UserProfile';
import Library from './pages/Library';
import Breathing from './pages/Breathing';
import Booking from './pages/Booking';
import UserFactsPage from './pages/UserFactsPage';
import Reports from './pages/Reports';
import Analyze from './pages/Analyze';
import Onboarding from './pages/Onboarding';
import BrandShowcase from './pages/BrandShowcase';
import TestEvaluation from './pages/TestEvaluation';
import TestResultDetail from './pages/TestResultDetail';
import TestResultsBrowse from './pages/TestResultsBrowse';
import FavoritesPage from './pages/FavoritesPage';
import VitalityPage from './pages/VitalityPage';
import VideoTest from './pages/VideoTest';
import AudioTest from './pages/AudioTest';
import LoginPage from './components/auth/LoginPage';
import AdminProtectedRoute from './components/auth/AdminProtectedRoute';
import RootRedirect from './components/RootRedirect';
import { ADMIN_ROLES, isAdminOnlyUser, getDefaultAdminRoute } from './types/roles';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AuthErrorBoundary from './components/auth/AuthErrorBoundary';
import { AuthorizationErrorBoundary } from './components/auth/AuthorizationErrorBoundary';
import BackendErrorHandler from './components/auth/BackendErrorHandler';
import { recordNativeDiagnostic } from './utils/nativeDiagnostics';
import './App.css';

/** Redirects admin index to the first available admin page based on roles */
const AdminIndexRedirect: React.FC = () => {
  const { user } = useAuth();
  return <Navigate to={getDefaultAdminRoute(user?.roles)} replace />;
};

/** Smart catch-all: admin-only users → /admin, others → /chat */
const CatchAllRedirect: React.FC = () => {
  const { user } = useAuth();
  if (isAdminOnlyUser(user?.roles)) {
    return <Navigate to="/admin" replace />;
  }
  return <Navigate to="/chat" replace />;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<LoginPage />} />
      <Route 
        path="/onboarding" 
        element={
          <ProtectedRoute requireOnboarding={false}>
            <Onboarding />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<RootRedirect />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="library" element={<Library />} />
        <Route path="breathing" element={<Breathing />} />
        <Route path="booking" element={<Booking />} />
        <Route path="user-facts" element={<UserFactsPage />} />
        <Route path="favorites" element={<FavoritesPage />} />
        <Route path="vitality" element={<VitalityPage />} />
        <Route path="profile" element={<UserProfile />} />
        <Route path="brand-showcase" element={<BrandShowcase />} />
        <Route path="videotest" element={<VideoTest />} />
        <Route path="audiotest" element={<AudioTest />} />
      </Route>

      {/* Admin Portal Routes */}
      <Route
        path="/admin"
        element={
          <AdminProtectedRoute>
            <AdminLayout />
          </AdminProtectedRoute>
        }
      >
        <Route index element={<AdminIndexRedirect />} />
        <Route 
          path="reports" 
          element={
            <AdminProtectedRoute requiredRole={ADMIN_ROLES.REPORTS_READER}>
              <Reports />
            </AdminProtectedRoute>
          } 
        />
        <Route 
          path="analyze" 
          element={
            <AdminProtectedRoute requiredRole={ADMIN_ROLES.SESSIONS_READER}>
              <Analyze />
            </AdminProtectedRoute>
          } 
        />
        <Route 
          path="test-evaluation" 
          element={
            <AdminProtectedRoute requiredRole={ADMIN_ROLES.TEST_CONTRIBUTOR}>
              <TestEvaluation />
            </AdminProtectedRoute>
          } 
        />
        <Route 
          path="test-evaluation/browse/:id?" 
          element={
            <AdminProtectedRoute requiredRole={ADMIN_ROLES.TEST_CONTRIBUTOR}>
              <TestResultsBrowse />
            </AdminProtectedRoute>
          } 
        />
        <Route 
          path="test-evaluation/:id" 
          element={
            <AdminProtectedRoute requiredRole={ADMIN_ROLES.TEST_CONTRIBUTOR}>
              <TestResultDetail />
            </AdminProtectedRoute>
          } 
        />
      </Route>

      <Route path="*" element={<CatchAllRedirect />} />
    </Routes>
  );
};

/**
 * Component that manages health service based on authentication state.
 * Only initializes health checks when user is authenticated and not on admin routes.
 */
const HealthServiceManager: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const isAdminRoute = location.pathname.startsWith('/admin');
    
    if (isAuthenticated && !isAdminRoute) {
      // Initialize health monitoring only when authenticated and not on admin routes
      healthService.initialize().catch((error) => {
        console.error('Health service initialization failed:', error);
      });
    } else {
      // Stop health checks when not authenticated or on admin routes
      healthService.stop();
    }

    return () => {
      healthService.stop();
    };
  }, [isAuthenticated, location.pathname]);

  return <>{children}</>;
};

const App: React.FC = () => {
  useEffect(() => {
    // Check URL for authentication errors first (MSAL)
    const hasUrlError = msalService.checkUrlForAuthErrors();
    
    if (!hasUrlError) {
      // Initialize MSAL only if no URL errors were found
      msalService.initialize().catch((error) => {
        authLogger.error('MSAL initialization failed:', error);
      });
    }

    // Initialize OIDC service if enabled
    if (OIDC_ENABLED) {
      authLogger.debug('OIDC is enabled, initializing OIDC service...');
      recordNativeDiagnostic('OIDC initialize() called');
      oidcService.initialize().then(() => {
        recordNativeDiagnostic('OIDC initialize() resolved');
      }).catch((error) => {
        authLogger.error('OIDC initialization failed:', error);
        const message = error instanceof Error ? error.message : String(error);
        recordNativeDiagnostic('OIDC initialize() failed', message);
        // Surface the failure so the login UI can stop showing the spinner.
        window.dispatchEvent(new CustomEvent('oidcInitError', { detail: { message } }));
      });
    }
  }, []);

  return (
    <MsalProvider instance={msalService.getInstance()}>
      <AuthErrorBoundary>
        <AuthProvider>
          <HealthServiceManager>
            <AuthorizationErrorBoundary>
              <UnifiedSignalRProvider>
                <ChatProvider>
                  <UserFavoritesProvider>
                    <BackendErrorHandler>
                      <AppRoutes />
                      {/* <SignalRMonitoringPanel /> */}
                    </BackendErrorHandler>
                  </UserFavoritesProvider>
                </ChatProvider>
              </UnifiedSignalRProvider>
            </AuthorizationErrorBoundary>
          </HealthServiceManager>
        </AuthProvider>
      </AuthErrorBoundary>
    </MsalProvider>
  );
};

export default App;
